import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaCloudUploadAlt,
  FaSpinner,
  FaBolt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import CatalogFitmentSelector, { emptyCatalogVehiclePath } from '../../../Vehicles/CatalogFitmentSelector';
import PartNameSearch from '../components/PartNameSearch';
import FitmentAttrsForm from '../components/FitmentAttrsForm';
import { validateFitmentAttrsClient } from '../utils/fitmentValidation';
import { authService, catalogNodeService } from '../../../../services/apiService';
import { getApiErrorMessage } from '../../../../utils/api';

const STEPS = [
  { id: 1, label: 'Part & vehicle' },
  { id: 2, label: 'Fitment & condition' },
  { id: 3, label: 'Price & publish' },
];

const DISPATCH_OPTIONS = [
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
  { value: 72, label: '72 hours' },
];

export default function NewPartListingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [oemValidating, setOemValidating] = useState(false);

  const [partName, setPartName] = useState('');
  const [selectedPart, setSelectedPart] = useState(null);
  const [vehiclePath, setVehiclePath] = useState(emptyCatalogVehiclePath());
  const [catalogResolve, setCatalogResolve] = useState(null);
  const [fitmentAttrs, setFitmentAttrs] = useState({});
  const [condition, setCondition] = useState('new_aftermarket');
  const [oemPhotoUrl, setOemPhotoUrl] = useState('');
  const [oemClaimAttempt, setOemClaimAttempt] = useState(1);
  const [price, setPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [dispatchWindowHours, setDispatchWindowHours] = useState(48);
  const [confirmHighPrice, setConfirmHighPrice] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [fitmentErrors, setFitmentErrors] = useState({});

  const profile = catalogResolve?.profile || selectedPart?.profile || 'B';
  const partCategory = partName.trim();

  const canAdvanceStep1 =
    partName.trim().length >= 2 &&
    vehiclePath.make &&
    vehiclePath.model &&
    vehiclePath.generation &&
    catalogResolve?.catalogNodeId;

  const canAdvanceStep2 = () => {
    if (condition === 'new_oem' && !oemPhotoUrl) return false;
    const errors = validateFitmentAttrsClient(profile, fitmentAttrs);
    return Object.keys(errors).length === 0;
  };

  const advanceToStep3 = () => {
    const errors = validateFitmentAttrsClient(profile, fitmentAttrs);
    if (Object.keys(errors).length) {
      setFitmentErrors(errors);
      toast.error('Complete all required fitment fields before continuing.');
      return;
    }
    setFitmentErrors({});
    setStep(3);
  };

  const canPublish = () => {
    const p = Number(price);
    const m = Number(mrp);
    const q = Number(quantity);
    return Number.isFinite(p) && p > 0 && Number.isFinite(m) && m > 0 && Number.isFinite(q) && q >= 1;
  };

  const handleOemPhoto = async (file) => {
    if (!file) return;
    try {
      setOemValidating(true);
      const uploadedUrl = await authService.uploadCompressedFile(file);
      setOemPhotoUrl(uploadedUrl);
      const res = await catalogNodeService.validateOemPhoto(uploadedUrl, partName);
      if (!res?.success && !res?.data?.valid) {
        toast.warning(res?.data?.reason || 'Label may need a retake — ensure brand and part number are readable.');
      } else {
        toast.success('Label photo uploaded');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not upload OEM photo'));
    } finally {
      setOemValidating(false);
    }
  };

  const buildPayload = useCallback(
    (overrides = {}) => ({
      partName: partName.trim(),
      partCategory,
      subSystem: catalogResolve?.subSystem || selectedPart?.subSystem,
      make: vehiclePath.make,
      model: vehiclePath.model,
      generation: vehiclePath.generation,
      variant: vehiclePath.variant,
      fuelType: vehiclePath.fuelType,
      transmission: vehiclePath.transmission,
      engineCode: vehiclePath.engineCode,
      fitmentAttrs,
      condition,
      oemPhotoUrl: condition === 'new_oem' ? oemPhotoUrl : undefined,
      oemClaimAttempt,
      price: Number(price),
      mrp: Number(mrp),
      quantity: Number(quantity),
      dispatchWindowHours,
      confirmHighPrice: confirmHighPrice || overrides.confirmHighPrice === true,
      ...overrides,
    }),
    [
      partName,
      partCategory,
      catalogResolve,
      selectedPart,
      vehiclePath,
      fitmentAttrs,
      condition,
      oemPhotoUrl,
      oemClaimAttempt,
      price,
      mrp,
      quantity,
      dispatchWindowHours,
      confirmHighPrice,
    ]
  );

  const handlePublish = async (forceConfirmHighPrice = false) => {
    try {
      setSubmitting(true);
      const res = await catalogNodeService.publishListing(
        buildPayload({ confirmHighPrice: forceConfirmHighPrice })
      );
      const data = res?.data || res;
      setPublishResult(data);

      if (data?.oemDowngraded) {
        toast.warning('Listed as aftermarket — OEM photo failed twice.');
      } else if (data?.pendingAdminApproval) {
        toast.info('Queued for admin approval (Profile E).');
      } else if (data?.pendingCatalogGap || data?.sellerMessage) {
        toast.info(data.sellerMessage || "This part type isn't in our catalog yet. We'll add it within 48h and notify you.");
      } else {
        toast.success('Listing is live!');
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      const status = err?.response?.status;

      if (code === 'OEM_PHOTO_RETAKE' || status === 422) {
        toast.warning(getApiErrorMessage(err, 'OEM photo unclear — please retake.'));
        setOemClaimAttempt(2);
        setStep(2);
        return;
      }
      if (code === 'FITMENT_ATTRS_REQUIRED' || code === 'FITMENT_VEHICLE_REQUIRED') {
        const fieldErrors = err?.response?.data?.fieldErrors || {};
        setFitmentErrors(fieldErrors);
        toast.error(getApiErrorMessage(err, 'Complete required fitment fields.'));
        setStep(2);
        return;
      }
      if (code === 'PRICE_HIGH_WARNING' || status === 409) {
        const ok = window.confirm(
          getApiErrorMessage(err, 'Price is high vs market reference. Publish anyway?')
        );
        if (ok) {
          setConfirmHighPrice(true);
          await handlePublish(true);
        }
        return;
      }
      if (code === 'DUPLICATE_LISTING') {
        toast.error(
          getApiErrorMessage(err, 'You already have an active listing for this part and condition.')
        );
        return;
      }
      if (code === 'GSTIN_BLOCKED' || code === 'GSTIN_VERIFICATION_FAILED') {
        toast.error(getApiErrorMessage(err));
        return;
      }
      toast.error(getApiErrorMessage(err, 'Could not publish listing'));
    } finally {
      setSubmitting(false);
    }
  };

  const listing = publishResult?.listing;
  const auto = publishResult?.systemAutoFilled;

  if (publishResult) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">
              <FaCheck />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Listing submitted</h2>
              <p className="text-sm text-slate-600">
                {publishResult.sellerMessage ||
                  (publishResult.pendingCatalogGap
                    ? "Queued — we'll add this part to the catalog within 48h and notify you."
                    : publishResult.pendingAdminApproval
                      ? 'Pending admin approval'
                      : 'Live on catalog node')}
              </p>
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-slate-500 text-xs">Listing ID</dt>
              <dd className="font-mono text-xs break-all">{listing?.listingId}</dd>
            </div>
            <div>
              <dt className="text-slate-500 text-xs">Reference price</dt>
              <dd>₹{auto?.marketPriceRef}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-slate-500 text-xs">Also fits</dt>
              <dd className="text-xs">{auto?.alsoFitsLabel || publishResult.alsoFitsLabel || '—'}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-slate-500 text-xs">Cross-compat tags</dt>
              <dd className="text-xs">{(auto?.crossCompatTags || []).join(', ') || '—'}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-3">
            {auto?.catalogNodeId && (
              <button
                type="button"
                onClick={() => navigate(`/catalog/node/${auto.catalogNodeId}`)}
                className="px-4 py-2 rounded-lg bg-white border border-emerald-300 text-sm font-medium text-emerald-800"
              >
                View catalog page
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/vendor/dashboard/inventory')}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold"
            >
              Manage inventory
            </button>
            <button
              type="button"
              onClick={() => {
                setPublishResult(null);
                setStep(1);
                setPartName('');
                setSelectedPart(null);
                setVehiclePath(emptyCatalogVehiclePath());
                setCatalogResolve(null);
                setFitmentAttrs({});
                setOemPhotoUrl('');
                setOemClaimAttempt(1);
                setPrice('');
                setMrp('');
                setConfirmHighPrice(false);
              }}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm"
            >
              List another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold mb-1">
            <FaBolt />
            3-click listing
          </div>
          <h1 className="text-2xl font-bold text-slate-900">List a new part</h1>
        </div>
        <button
          type="button"
          onClick={() => navigate('/vendor/dashboard/products')}
          className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
        >
          <FaArrowLeft /> Back
        </button>
      </div>

      <ol className="flex gap-2">
        {STEPS.map((s) => (
          <li
            key={s.id}
            className={`flex-1 rounded-lg border px-3 py-2 text-center text-xs font-semibold ${
              step === s.id
                ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                : step > s.id
                  ? 'border-emerald-200 bg-emerald-50/50 text-emerald-700'
                  : 'border-slate-200 text-slate-400'
            }`}
          >
            {s.id}. {s.label}
          </li>
        ))}
      </ol>

      {step === 1 && (
        <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <PartNameSearch
            value={partName}
            onChange={setPartName}
            onSelect={setSelectedPart}
            vehiclePath={vehiclePath}
          />
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Vehicle *</p>
            <CatalogFitmentSelector
              value={vehiclePath}
              partCategory={partCategory}
              subSystem={selectedPart?.subSystem || catalogResolve?.subSystem}
              onChange={setVehiclePath}
              onResolved={setCatalogResolve}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              disabled={!canAdvanceStep1}
              onClick={() => setStep(2)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              Next <FaArrowRight />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div>
            <FitmentAttrsForm
              partCategory={partCategory}
              profile={profile}
              value={fitmentAttrs}
              onChange={(next) => {
                setFitmentAttrs(next);
                setFitmentErrors(validateFitmentAttrsClient(profile, next));
              }}
              fieldErrors={fitmentErrors}
              vehiclePath={vehiclePath}
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Condition *</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'new_oem', label: 'New OEM' },
                { value: 'new_aftermarket', label: 'New aftermarket' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCondition(opt.value)}
                  className={`p-3 rounded-lg border text-sm font-semibold ${
                    condition === opt.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {condition === 'new_oem' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4 space-y-3">
              <p className="text-xs text-amber-900">
                Upload label close-up (brand + part number readable).
                {oemClaimAttempt > 1 ? ' Last retake before downgrade.' : ''}
              </p>
              <label className="flex flex-col items-center gap-2 border-2 border-dashed border-amber-300 rounded-lg p-6 cursor-pointer">
                {oemValidating ? (
                  <FaSpinner className="animate-spin text-amber-600 text-2xl" />
                ) : (
                  <FaCloudUploadAlt className="text-amber-600 text-2xl" />
                )}
                <span className="text-xs font-medium">{oemPhotoUrl ? 'Change photo' : 'Upload label'}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={oemValidating}
                  onChange={(e) => handleOemPhoto(e.target.files?.[0])}
                />
              </label>
              {oemPhotoUrl && (
                <img src={oemPhotoUrl} alt="OEM label" className="max-h-32 rounded border mx-auto" />
              )}
            </div>
          )}

          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(1)} className="px-4 py-2 border rounded-lg text-sm">
              Back
            </button>
            <button
              type="button"
              disabled={!canAdvanceStep2()}
              onClick={advanceToStep3}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              Next <FaArrowRight />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            Price must be at least 20% of the platform reference price and cannot exceed MRP.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Price (₹) *</label>
              <input
                type="number"
                min="1"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">MRP (₹) *</label>
              <input
                type="number"
                min="1"
                value={mrp}
                onChange={(e) => setMrp(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Quantity *</label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Dispatch window</label>
              <select
                value={dispatchWindowHours}
                onChange={(e) => setDispatchWindowHours(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                {DISPATCH_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-xs text-slate-500 rounded-lg bg-slate-50 p-3 space-y-1">
            <p>
              <strong>{partName}</strong> · {vehiclePath.make} {vehiclePath.model}
            </p>
            <p>Condition: {condition} · Profile {profile}</p>
          </div>

          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(2)} className="px-4 py-2 border rounded-lg text-sm">
              Back
            </button>
            <button
              type="button"
              disabled={!canPublish() || submitting}
              onClick={() => handlePublish(false)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              {submitting ? <FaSpinner className="animate-spin" /> : <FaCheck />}
              Publish
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
